/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================
*/
import { useMemo } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Shared sections
import ContactUsSection from "pages/LandingPages/AboutUs/sections/contactUs";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import heroImage from "assets/images/tall-office-modern-buildings.jpg";
import screedingImage from "assets/images/screeding.png";
import cuttingImage from "assets/images/Image_showing_concrete_diamond_cutting.jpeg";
import cleaningImage from "assets/images/site_cleaning.jpg";
import constructionLogistics from "assets/images/construction_logistics.png";

function OurServices() {
  const coreServices = useMemo(
    () => [
      {
        id: "screeding",
        title: "Concrete screeding",
        image: screedingImage,
        icon: "grid_view",
        summary: "Level, durable screeds that set the standard for every finish that follows.",
        bullets: [
          "Laser-guided and hand-applied solutions for commercial, industrial, and residential projects.",
          "Tight tolerances for high-traffic areas, racking systems, and specialist floor finishes.",
          "Careful planning around curing, access, and follow-on trades to protect your programme.",
        ],
      },
      {
        id: "cutting",
        title: "Concrete diamond cutting and core drilling",
        image: cuttingImage,
        icon: "content_cut",
        summary: "Accurate structural openings and modifications, delivered with control and care.",
        bullets: [
          "Wall, floor, and diamond cutting for doorways, service penetrations, and design changes.",
          "Dust and slurry management to keep surrounding areas clean and safe.",
          "Core drilling for service penetrations",
          "Methodical setting-out so every opening lands exactly where your drawings demand.",
        ],
      },
      {
        id: "cleaning",
        title: "Project handover cleaning",
        image: cleaningImage,
        icon: "cleaning_services",
        summary: "Handover-ready spaces that feel finished, not just ‘nearly there’.",
        bullets: [
          "Deep cleaning of floors, walls, and high-touch surfaces before inspections and sign-off.",
          "Removal of construction residues, packaging, and leftover materials.",
          "Staged cleaning to support phased handovers on live or multi-zone projects.",
        ],
      },
      {
        id: "logistics",
        title: "Construction logistics",
        image: constructionLogistics,
        icon: "group",
        summary: "We manage your construction site and Supply resources.",
        bullets: [
          "We provid strategic planning and management of all resources—materials, equipment, and people—moving to, from, and around a construction site to ensure projects stay on schedule, budget, and meet quality standards, preventing costly delays through efficient coordination of deliveries, storage, site layout, and workforce movement.",
        ],
      },
    ],
    []
  );

  const whyChooseUs = useMemo(
    () => [
      {
        title: "Specialist crews",
        description:
          "Teams who focus on screeding, cutting, and handover cleaning day in, day out—so you get consistent quality.",
        icon: "group",
      },
      {
        title: "Site-aware planning",
        description:
          "We design our work around your access windows, neighbours, and next trades to reduce friction on site.",
        icon: "event_available",
      },
      {
        title: "Reliable communication",
        description:
          "Clear updates before, during, and after every visit so you always know what has been completed and what’s next.",
        icon: "chat",
      },
    ],
    []
  );

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/pages/landing-pages/contact-us",
          label: "Contact us",
          color: "default",
        }}
        transparent
        light
      />

      {/* Header / hero */}
      <MKBox
        minHeight="75vh"
        width="100%"
        position="relative"
        overflow="hidden"
        display="grid"
        placeItems="center"
      >
        <MKBox
          aria-hidden
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          sx={({ functions: { linearGradient, rgba }, palette: { gradients } }) => ({
            backgroundImage: `${linearGradient(
              rgba(gradients.dark.main, 0.7),
              rgba(gradients.dark.state, 0.7)
            )}, url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
          })}
        />
        <Container sx={{ height: "80%", display: "flex", alignItems: "center" }}>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center", position: "relative", zIndex: 1 }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Our services
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.85} mt={1} mb={3}>
              Practical, specialist services that keep your project on track—from first screed to
              final clean.
            </MKTypography>
            <MKButton
              color="default"
              sx={{ color: ({ palette: { dark } }) => dark.main }}
              component="a"
              href="#services"
            >
              Explore services
            </MKButton>
          </Grid>
        </Container>
      </MKBox>

      {/* Main content */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 0,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Overview */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <MKTypography variant="h3" mb={1}>
                  Built around the realities of site work
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  We focus on the practical services that make the rest of your programme possible:
                  flat floors, precise openings, and clean, safe spaces ready for handover.
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  Each service is delivered by specialist teams who understand construction
                  environments, health & safety, and the pressures of fixed deadlines.
                </MKTypography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <MKBox
                  component="img"
                  src={screedingImage}
                  alt="Screeding and site preparation"
                  width="100%"
                  sx={({ borders: { borderRadius }, boxShadows: { md } }) => ({
                    borderRadius: borderRadius.lg,
                    boxShadow: md,
                    objectFit: "cover",
                    maxHeight: 380,
                  })}
                />
              </Grid> */}
            </Grid>
          </Container>
        </MKBox>

        {/* Detailed services */}
        <MKBox id="services" component="section" py={6} bgColor="grey-100">
          <Container>
            <Grid container item xs={12} md={8} sx={{ mx: "auto", textAlign: "left", mb: 4 }}>
              <MKTypography variant="h3" mb={1}>
                Services in detail
              </MKTypography>
              <MKTypography variant="body1" color="text">
                A closer look at what we do, how we do it, and where each service fits into your
                project.
              </MKTypography>
            </Grid>

            <Grid container spacing={4}>
              {coreServices.map((service, index) => (
                <Grid
                  key={service.id}
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: index % 2 === 0 ? "row" : "row-reverse",
                    flexWrap: "wrap",
                    mb: 4,
                  }}
                >
                  <Grid item xs={12} md={6}>
                    <MKBox
                      component="img"
                      src={service.image}
                      alt={service.title}
                      width="100%"
                      sx={({ borders: { borderRadius }, boxShadows: { md } }) => ({
                        borderRadius: borderRadius.lg,
                        boxShadow: md,
                        objectFit: "cover",
                        maxHeight: 360,
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MKBox
                      height="100%"
                      p={{ xs: 2, md: 4 }}
                      sx={({ palette: { background } }) => ({
                        backgroundColor: background.paper,
                      })}
                    >
                      <MKBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width="3rem"
                        height="3rem"
                        borderRadius="lg"
                        variant="gradient"
                        bgColor="info"
                        coloredShadow="info"
                        mb={2}
                      >
                        <Icon sx={{ color: "white" }}>{service.icon}</Icon>
                      </MKBox>
                      <MKTypography variant="h4" mb={1}>
                        {service.title}
                      </MKTypography>
                      <MKTypography variant="body1" color="text" mb={2}>
                        {service.summary}
                      </MKTypography>
                      {service.bullets.map((item) => (
                        <MKTypography key={item} variant="body2" color="text" mb={0.5}>
                          • {item}
                        </MKTypography>
                      ))}
                    </MKBox>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Why work with us */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} md={8} sx={{ mx: "auto", textAlign: "left", mb: 4 }}>
              <MKTypography variant="h3" mb={1}>
                Why clients choose our services
              </MKTypography>
              <MKTypography variant="body1" color="text">
                Beyond tools and machinery, it’s the way we plan, deliver, and communicate that
                keeps projects running smoothly.
              </MKTypography>
            </Grid>
            <Grid container spacing={3}>
              {whyChooseUs.map(({ title, description, icon }) => (
                <Grid key={title} item xs={12} md={4}>
                  <MKBox
                    height="100%"
                    borderRadius="lg"
                    p={3}
                    sx={({ palette: { background }, boxShadows: { md } }) => ({
                      backgroundColor: background.paper,
                      boxShadow: md,
                    })}
                  >
                    <MKBox
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      width="3rem"
                      height="3rem"
                      borderRadius="lg"
                      variant="gradient"
                      bgColor="info"
                      coloredShadow="info"
                      mb={2}
                    >
                      <Icon sx={{ color: "white" }}>{icon}</Icon>
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {description}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
      </Card>

      {/* Contact */}
      <MKBox id="contact-us" />
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: 4,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <ContactUsSection />
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default OurServices;
